import type { ReactElement, ReactNode } from 'react'

/** White, hairline, radius 16, the faintest lift. Every panel on the page. */
export function Card({
  children,
  className,
  pad = 'default',
}: {
  children: ReactNode
  className?: string
  /** `tight` is the narrower side column; `flush` pads its own rows instead. */
  pad?: 'default' | 'tight' | 'flush'
}): ReactElement {
  return (
    <section className={className ? `nw-card ${className}` : 'nw-card'} data-pad={pad}>
      {children}
    </section>
  )
}

export function CardHead({
  title,
  lede,
  aside,
  id,
}: {
  title: string
  /**
   * The sentence under the title, where a card's contents need one. Several of
   * these pages open with a paragraph that is the only thing stopping a figure
   * being misread, and it belongs against the figure rather than at the top of
   * the page.
   */
  lede?: ReactNode
  /** Whatever sits at the other end of the title's baseline. */
  aside?: ReactNode
  id?: string
}): ReactElement {
  return (
    <div className="nw-card-head">
      <div className="nw-card-title">
        <h2 id={id}>{title}</h2>
        {lede !== undefined && <p className="nw-card-lede">{lede}</p>}
      </div>
      {aside !== undefined && <div className="nw-card-aside">{aside}</div>}
    </div>
  )
}

/**
 * A label, a figure and a note.
 *
 * The help dot is a button rather than a decoration: it carries the sentence
 * that says what the figure means, which every one of these figures needs and
 * none of them has room for.
 */
export function StatCard({
  label,
  help,
  children,
  note,
}: {
  label: string
  help?: string
  children: ReactNode
  note?: ReactNode
}): ReactElement {
  return (
    <section className="nw-card nw-stat">
      <div className="nw-stat-label">
        <span>{label}</span>
        {help !== undefined && (
          <button type="button" className="nw-help" title={help} aria-label={help}>
            ?
          </button>
        )}
      </div>
      <div className="nw-stat-value">{children}</div>
      {note !== undefined && <div className="nw-stat-note">{note}</div>}
    </section>
  )
}

/**
 * The chart lives inside the card that carries the headline figure, under it,
 * so the number and its history are one object rather than two.
 */
export function ChartCard({
  children,
  label,
  aside,
}: {
  children: ReactNode
  label?: string
  aside?: ReactNode
}): ReactElement {
  return (
    <div className="nw-plot-panel">
      {(label !== undefined || aside !== undefined) && (
        <div className="nw-plot-head">
          <span>{label}</span>
          {aside}
        </div>
      )}
      {children}
    </div>
  )
}

/** A small white card floating over a chart, saying what a shape on it means. */
export function Callout({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="nw-callout">
      <span className="nw-callout-label">{label}</span>
      <span className="nw-callout-value">{children}</span>
    </div>
  )
}
